/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ComponentType, Suspense } from 'react';

import { jsx } from '@compiled/react';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import IconTile from '@atlaskit/icon/icon-tile';
import type { IconTileProps, NewCoreIconProps } from '@atlaskit/icon/types';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { GenericInsightCell, type GenericInsightCellPosition } from './generic-insight-cell';
import type { InsightSubItem } from './generic-insight-cell/types';
import { TrendingContentCell, type TrendingContentCellPosition } from './trending-content-cell';

interface InsightItem {
	id: string;
	title: string;
	category: string;
	icon: ComponentType<NewCoreIconProps>;
	iconAppearance: IconTileProps['appearance'];
	count: number;
	subItems?: InsightSubItem[];
}

const styles = cssMap({
	// Single outer container — no border, transparent background
	groupItem: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
		paddingBlockStart: token('space.100'),
	},
	headerButton: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.150'),
		paddingInlineStart: 0,
		paddingInlineEnd: 0,
	},
	headerRow: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
	titleSection: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		flexGrow: 0,
		overflow: 'hidden',
	},
	// Inner cards container — single border around the entire group.
	// overflow:hidden clips the first/last card's corners to this inner container's borderRadius.
	// Individual cards have bottom borders as delimiters between cards (not on last card).
	subItemsContainer: {
		display: 'flex',
		flexDirection: 'column',
		marginBlockStart: 0,
		marginBlockEnd: token('space.200'),
		marginInlineStart: 0,
		marginInlineEnd: 0,
		borderRadius: token('radius.xlarge'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		overflow: 'hidden',
	},
});

export interface InsightsFeedGroupProps {
	/** The insight item data to render */
	item: InsightItem;
	/** Callback when an individual insight card is pressed */
	onInsightPress: (insightId: string) => void;
	/** Optional content to render below the group header (e.g. debug panels) */
	headerExtra?: React.ReactNode;
	/** Optional content to render after each sub-item */
	renderSubItemExtra?: (subItem: InsightSubItem) => React.ReactNode;
	/** Whether the feed is rendered in fullscreen layout. */
	isFullScreen?: boolean;
	/** Optional callback invoked when a link card within any cell is clicked. */
	onLinkClick?: (url: string) => void;
}

/**
 * InsightsFeedGroup — Renders a single insight category group card.
 * Shows a coloured icon tile, category name, and a list of insights.
 * Groups are always expanded and non-collapsible — they display the header PLUS a list of GenericInsightCell or TrendingContentCell sub-components.
 */
export const InsightsFeedGroup = ({
	item,
	onInsightPress,
	headerExtra,
	renderSubItemExtra,
	isFullScreen = false,
	onLinkClick,
}: InsightsFeedGroupProps): JSX.Element => {
	return (
		<Box xcss={styles.groupItem} testId={`insights-feed-group-${item.category}`}>
			<Box xcss={styles.headerButton}>
				<Flex xcss={styles.headerRow}>
					<Suspense fallback={null}>
						<IconTile
							icon={item.icon}
							label={item.title}
							appearance={item.iconAppearance}
							size="small"
						/>
					</Suspense>
					<Flex xcss={styles.titleSection}>
						<Heading size="xsmall">{item.title}</Heading>
					</Flex>
				</Flex>
			</Box>
			{headerExtra}
			<Box xcss={styles.subItemsContainer} testId={`insights-feed-group-items-${item.category}`}>
				{item.subItems?.map((subItem, index) => {
					const total = item.subItems!.length;
					let position: GenericInsightCellPosition | TrendingContentCellPosition;
					if (total === 1) {
						position = 'only';
					} else if (index === 0) {
						position = 'first';
					} else if (index === total - 1) {
						position = 'last';
					} else {
						position = 'middle';
					}

					if (subItem.layout === 'trending-content') {
						return (
							<TrendingContentCell
								key={subItem.id}
								item={{
									...subItem,
									metricValue: subItem.metricValue ?? '',
									metricUnit: subItem.metricUnit ?? '',
								}}
								position={position}
								onInsightPress={onInsightPress}
								isFullScreen={isFullScreen}
								onLinkClick={onLinkClick}
							/>
						);
					}

					return (
						<Box key={subItem.id}>
							<GenericInsightCell
								item={subItem}
								position={position}
								onInsightPress={onInsightPress}
								isFullScreen={isFullScreen}
								onLinkClick={onLinkClick}
							/>
							{renderSubItemExtra?.(subItem)}
						</Box>
					);
				})}
			</Box>
		</Box>
	);
};
